/* eslint-disable react/display-name */
import React, { ForwardedRef, memo, useCallback, useMemo } from 'react';
import { TableVirtuoso } from 'react-virtuoso';

import {
  Avatar,
  CircularProgress,
  ClassNameMap,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useFeatureFlag } from 'modules/common/hooks';
import { useAvatar } from 'modules/App/hooks/useAvatar';
import { useFormattedDuration } from 'modules/notes/common';
import { useScrollbarStyles } from 'modules/common';
import TeamTableNoData from './TeamTableNoData';

type StyleProps = {
  isTiwScreenReaderEnabled: boolean;
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: ({
      dynamicHeight,
      isDataEmpty
    }: {
      dynamicHeight: number;
      isDataEmpty: boolean;
    }) => (isDataEmpty ? theme.spacing(31) : theme.spacing(dynamicHeight))
  },
  table: {
    '@supports ( -moz-appearance:none )': {
      height: 'unset'
    },
    height: 'max-content',
    paddingBottom: theme.spacing(1),
    tableLayout: 'fixed'
  },

  tableRow: {
    height: '100%'
  },
  nameHeader: {
    width: theme.spacing(33),
    zIndex: '3',
    marginRight: theme.spacing(8)
  },
  nameHeaderDiv: {
    display: 'flex',
    justifyContent: 'space-between',
    color: theme.palette.text.secondary
  },
  otherHeader: {
    width: theme.spacing(16),
    color: theme.palette.text.secondary,
    fontWeight: '600'
  },
  cell: {
    borderRight: '1px solid rgba(224, 224, 224, 1)'
  },
  dataCell: {
    padding: theme.spacing(0.2, 0.5),
    textAlign: 'center'
  },
  totalCell: {
    color: theme.palette.text.secondary
  },
  sticky: {
    position: 'sticky',
    left: 0,
    background: 'white'
  },
  footer: {
    padding: theme.spacing(1, 0),
    display: 'flex',
    justifyContent: 'center'
  },
  footerText: {
    paddingLeft: theme.spacing(1)
  }
}));

const nameRowStyles = makeStyles((theme) => ({
  avatar: {
    backgroundColor: theme.palette.success.main,
    color: ({ isTiwScreenReaderEnabled }: StyleProps) =>
      isTiwScreenReaderEnabled
        ? theme.palette.getContrastText(theme.palette.success.main)
        : undefined,
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: theme.typography.body1.fontSize,
    alignSelf: 'center'
  },
  nameCell: {
    padding: theme.spacing(1, 2),
    zIndex: '1',
    height: '100%'
  },
  divCell: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  },
  text: {
    marginLeft: theme.spacing(1),
    alignSelf: 'center'
  },

  span: {
    display: 'flex',
    justifyContent: 'space-around',
    marginRight: theme.spacing(3),
    textAlign: 'left'
  },
  totalText: {
    color: theme.palette.text.secondary,
    alignSelf: 'center',
    textAlign: 'right',
    whiteSpace: 'nowrap'
  }
}));

type TableCellAvatarProp = {
  name: string;
  total: number;
  tableClasses: ClassNameMap;
  lastCell: boolean;
};

const TableCellAvatar: React.FC<TableCellAvatarProp> = ({
  name,
  total,
  tableClasses,
  lastCell
}: TableCellAvatarProp) => {
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const classes = nameRowStyles({ isTiwScreenReaderEnabled });
  const splitName = name.split(',');
  const lastName = (splitName[1] || '').trim();
  const firstName = (splitName[0] || ' ').trim();
  const { avatar } = useAvatar({
    firstName: firstName,
    lastName: lastName
  });
  const initial = firstName.trim()[0];
  const formattedDuration = useFormattedDuration({
    needsMask: true,
    duration: total ?? 0
  });
  return (
    <TableCell
      className={classNames(
        classes.nameCell,
        tableClasses.sticky,
        tableClasses.cell
      )}
      data-testid={`team-table-data-name-${firstName}`}
      sx={{
        borderBottom: lastCell ? '0px' : '1px solid rgba(224, 224, 224, 1)'
      }}
    >
      <div className={classes.divCell}>
        <span className={classes.span}>
          <Avatar
            className={classes.avatar}
            data-testid={`team-table-avatar-${firstName}`}
          >
            {firstName && lastName ? avatar : initial}
          </Avatar>
          <Typography
            variant="body1"
            className={classes.text}
            data-testid={`team-table-${firstName}`}
          >
            {name}
          </Typography>
        </span>
        <span className={classes.totalText}>
          <Typography variant="body2">{formattedDuration}</Typography>
        </span>
      </div>
    </TableCell>
  );
};

type TeamTableProps = {
  columns: string[];
  data: any[];
  loadMore: () => Promise<void>;
  moreLoading: boolean;
  isEmpty: boolean;
  cellComponent: (info: any) => JSX.Element;
};

const TeamTable: React.FC<TeamTableProps> = ({
  columns,
  data,
  loadMore,
  moreLoading,
  cellComponent,
  isEmpty
}: TeamTableProps) => {
  const dynamicHeight = useMemo(() => {
    const d = data.length || 0;
    let base = 0;
    if (columns.length <= 7) base = 16.5;
    else base = 18;

    if (d >= 7) {
      return base + 6 * 6.1;
    } else return base + (d - 1) * 6.1;
  }, [columns.length, data]);
  const isDataEmpty = useMemo(() => isEmpty && data.length === 0, [
    data.length,
    isEmpty
  ]);

  const classes = useStyles({ dynamicHeight, isDataEmpty });
  const classesScrollbar = useScrollbarStyles();

  const TableComponents = useMemo(
    () => ({
      Scroller: React.forwardRef((props, ref: ForwardedRef<HTMLDivElement>) => (
        <TableContainer
          component={Paper}
          {...props}
          ref={ref}
          className={classNames(classesScrollbar.scrollbar)}
          data-testid={'my-team-table-container'}
        />
      )),
      Table: (props: any) => (
        <Table
          aria-label="Team Table"
          stickyHeader
          size={'small'}
          data-testid={'my-team-table'}
          className={classes.table}
          {...props}
        />
      ),
      TableHead: React.forwardRef(
        (props, ref: ForwardedRef<HTMLTableSectionElement>) => (
          <TableHead
            data-testid={'my-team-table-header'}
            {...props}
            ref={ref}
          />
        )
      ),
      TableRow: (props: any) => (
        <TableRow
          data-testid={'team-table-data-row'}
          {...props}
          className={classes.tableRow}
        />
      ),
      TableBody: React.forwardRef(
        (props, ref: ForwardedRef<HTMLTableSectionElement>) => (
          <TableBody
            {...props}
            data-testid={'my-team-table-table-body'}
            ref={ref}
          />
        )
      ),
      TableFooter: (props: any) => {
        <TableFooter data-testid={'team-table-data-row'} {...props} />;
      }
    }),
    [classes.table, classes.tableRow, classesScrollbar.scrollbar]
  );

  const more = useCallback(async () => {
    await loadMore();
  }, [loadMore]);

  return (
    <Paper
      className={classes.container}
      elevation={0}
      data-testid={'my-team-container'}
    >
      {isEmpty && data.length === 0 ? (
        <TeamTableNoData />
      ) : (
        <TableVirtuoso
          data={data}
          endReached={more}
          className={classesScrollbar.scrollbar}
          components={TableComponents}
          style={{
            height: '100%'
          }}
          overscan={30}
          fixedHeaderContent={() => (
            <TableRow>
              <TableCell
                className={classNames(
                  classes.nameHeader,
                  classes.sticky,
                  classes.cell
                )}
                data-testid="team-table-name-total-column"
              >
                <div className={classes.nameHeaderDiv}>
                  <span>
                    <FormattedMessage id="insights.teamTable.nameColumn" />
                  </span>
                  <span>
                    <FormattedMessage id="insights.teamTable.total" />
                  </span>
                </div>
              </TableCell>

              {columns.map((i) => (
                <TableCell
                  className={classNames(classes.otherHeader, classes.cell)}
                  align="center"
                  key={i}
                  data-testid={`team-table-${i}-column`}
                >
                  {i}
                </TableCell>
              ))}
            </TableRow>
          )}
          itemContent={(index, user) => {
            return (
              <>
                <TableCellAvatar
                  name={user?.name || ''}
                  total={user?.total || 0}
                  tableClasses={classes}
                  lastCell={index === data.length - 1}
                />

                {Object.keys(user?.data || {}).map((i) => (
                  <TableCell
                    align="center"
                    className={classNames(classes.cell, classes.dataCell)}
                    key={`${i}-${user?.data[i].actualHours}`}
                    data-testid={`team-table-cell-${user?.name}-${i}`}
                    sx={{
                      borderBottom:
                        index === data.length - 1
                          ? '0px'
                          : '1px solid rgba(224, 224, 224, 1)'
                    }}
                  >
                    {cellComponent(user?.data[i])}
                  </TableCell>
                ))}
              </>
            );
          }}
          fixedFooterContent={() => {
            {
              return moreLoading ? (
                <TableRow>
                  <TableCell
                    align="center"
                    colSpan={100}
                    data-testid="more-loading-cell"
                  >
                    <div className={classes.footer}>
                      <CircularProgress
                        size={18}
                        data-testid="more-loading-circular"
                      />
                      <Typography
                        variant="body2"
                        className={classes.footerText}
                        data-testid="more-loading-text"
                      >
                        <FormattedMessage id="insights.teamTable.loading" />
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <></>
              );
            }
          }}
        />
      )}
    </Paper>
  );
};
export default memo(TeamTable);
